import type { MemoryDraft } from "./memory-drafts";
import type { MemoryAdd, MemoryRecord, SettingsChanges, SettingsSnapshot } from "./types";

export type EditableSettings = Pick<SettingsSnapshot,
  | "system_prompt"
  | "style_guide"
  | "image_model"
  | "image_cfg"
  | "image_sampler"
  | "image_scheduler"
  | "image_resolution"
  | "selected_persona"
  | "title"
  | "title_enabled"
>;

export interface EditableMemoryState extends MemoryRecord {
  deleted: boolean;
  originalMemory: string;
  originalPermanent: boolean;
}

export function toEditableSettings(snapshot: SettingsSnapshot): EditableSettings {
  return {
    system_prompt: snapshot.system_prompt,
    style_guide: snapshot.style_guide,
    image_model: snapshot.image_model,
    image_cfg: snapshot.image_cfg,
    image_sampler: snapshot.image_sampler,
    image_scheduler: snapshot.image_scheduler,
    image_resolution: snapshot.image_resolution,
    selected_persona: snapshot.selected_persona,
    title: snapshot.title,
    title_enabled: snapshot.title_enabled,
  };
}

export function isPersonaUnlocked(snapshot: SettingsSnapshot, name: string): boolean {
  return snapshot.unlocked_personas.some((persona) => persona.name === name);
}

export function settingsValidationError(values: Pick<EditableSettings, "title" | "title_enabled">): string {
  if (values.title_enabled && values.title.trim().length === 0) return "启用头衔后，请先填写你喜欢的头衔。";
  if ([...values.title].length > 100) return "头衔不能超过100个字。";
  return "";
}

export function createSettingsChanges(
  saved: SettingsSnapshot,
  values: EditableSettings,
  memories: EditableMemoryState[],
  drafts: MemoryDraft[],
  requestId: string,
): SettingsChanges {
  if (saved.persona_available && !isPersonaUnlocked(saved, values.selected_persona)) {
    throw new Error("selected_persona_not_unlocked");
  }

  const changes: SettingsChanges = {
    memory_remove: memories
      .filter((memory) => memory.deleted || memory.memory !== memory.originalMemory || memory.permanent !== memory.originalPermanent)
      .map((memory) => memory.id),
    memory_add: [
      ...memories
        .filter((memory) => !memory.deleted && (memory.memory !== memory.originalMemory || memory.permanent !== memory.originalPermanent))
        .map<MemoryAdd>((memory) => ({ permanent: memory.permanent, content: memory.memory.trim() })),
      ...drafts
        .filter((draft) => draft.content.trim())
        .map<MemoryAdd>((draft) => ({ permanent: draft.permanent, content: draft.content.trim() })),
    ].filter((memory) => memory.content),
    system_prompt: values.system_prompt,
    style_guide: values.style_guide,
    image_model: values.image_model,
    image_cfg: values.image_cfg,
    image_sampler: values.image_sampler,
    image_scheduler: values.image_scheduler,
    image_resolution: values.image_resolution,
    title: values.title,
    title_enabled: values.title_enabled,
    request_id: requestId,
    expected_revision: saved.revision,
  };
  if (saved.persona_available) changes.selected_persona = values.selected_persona;
  return changes;
}

export function isSettingsDirty(
  saved: SettingsSnapshot,
  values: EditableSettings,
  memories: EditableMemoryState[],
  drafts: MemoryDraft[],
): boolean {
  return memories.some((memory) => memory.deleted || memory.memory !== memory.originalMemory || memory.permanent !== memory.originalPermanent)
    || drafts.some((draft) => draft.content.trim().length > 0)
    || Object.entries(values).some(([key, value]) => value !== saved[key as keyof EditableSettings]);
}
